import { Rocket, ShieldCheck, Tag, Target, Sparkles, AlertCircle } from 'lucide-react';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import type { ApprovalPreset, CategoryEntry, WelcomeMission } from './useWelcomeState';

interface Props {
  approvalPreset: ApprovalPreset | '';
  brandDescription: string;
  productType: string;
  categories: CategoryEntry[];
  mission: WelcomeMission | '';
  researchBrands: string[];
  isLaunching: boolean;
  launchError: string | null;
  onLaunch: () => void;
}

const PRESET_LABELS: Record<ApprovalPreset, string> = {
  full_manual: 'Full Manual',
  suggest_only: 'Suggest Only',
  auto_with_review: 'Auto with Review',
  fully_autonomous: 'Fully Autonomous',
};

function missionPreview(mission: WelcomeMission | '', categories: CategoryEntry[], researchBrands: string[]): string {
  const catNames = categories.map((c) => c.name).join(', ');
  switch (mission) {
    case 'discovery':
      return `Dippy will spend ~15 min searching for dropship suppliers across ${catNames || 'your categories'} and rank the top 20–25 for you.`;
    case 'marketplace_sourcing':
      return `Dippy will source manufacturers on Alibaba and 1688 for ${catNames || 'your categories'} — roughly 20 min of deep marketplace research.`;
    case 'research':
      return `Dippy will research ${researchBrands.join(', ') || 'the brands you named'} — wholesale programs, contacts, dropship terms. About 8 min.`;
    case 'skip':
      return 'Your settings will be saved, but Dippy will stand by until you give him a mission from the AI Agent dashboard.';
    default:
      return '';
  }
}

export default function WelcomeStep5Launch({
  approvalPreset,
  brandDescription,
  productType,
  categories,
  mission,
  researchBrands,
  isLaunching,
  launchError,
  onLaunch,
}: Props) {
  const isSkip = mission === 'skip';

  return (
    <div className="space-y-6">
      <p className="text-sm text-text-secondary">
        Review your choices. Dippy will use this setup for his first mission and every mission after.
      </p>

      <Card className="p-6 space-y-5">
        {/* Autonomy */}
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-9 h-9 rounded-lg bg-sage-100 flex items-center justify-center">
            <ShieldCheck className="w-4 h-4 text-sage-700" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-wide text-text-muted mb-0.5">Autonomy Level</div>
            <div className="font-semibold text-text-primary">
              {approvalPreset ? PRESET_LABELS[approvalPreset] : '—'}
            </div>
          </div>
        </div>

        {/* Brand */}
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-9 h-9 rounded-lg bg-sage-100 flex items-center justify-center">
            <Tag className="w-4 h-4 text-sage-700" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-wide text-text-muted mb-0.5">Brand</div>
            <div className="text-sm text-text-primary line-clamp-3">{brandDescription || '—'}</div>
            <div className="text-xs text-text-secondary mt-1 italic">{productType}</div>
          </div>
        </div>

        {/* Categories */}
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-9 h-9 rounded-lg bg-sage-100 flex items-center justify-center">
            <Target className="w-4 h-4 text-sage-700" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-wide text-text-muted mb-1">Priority Categories</div>
            <div className="flex flex-wrap gap-1.5">
              {categories.length === 0 ? (
                <span className="text-sm text-text-muted">—</span>
              ) : (
                categories.map((c) => (
                  <span key={c.name} className="px-2.5 py-0.5 bg-sage-100 text-sage-800 text-xs rounded-full">
                    {c.name}
                  </span>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Mission */}
        <div className="flex items-start gap-3 pt-4 border-t border-stone-200">
          <div className="shrink-0 w-9 h-9 rounded-lg bg-sage-600 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-wide text-text-muted mb-0.5">Dippy's First Mission</div>
            <div className="text-sm text-text-primary leading-relaxed">
              {missionPreview(mission, categories, researchBrands)}
            </div>
          </div>
        </div>
      </Card>

      {launchError && (
        <Card className="p-4 border-red-200 bg-red-50">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
            <div className="text-sm text-red-800">{launchError}</div>
          </div>
        </Card>
      )}

      <div className="flex justify-center pt-2">
        <Button
          variant="boutique"
          onClick={onLaunch}
          isLoading={isLaunching}
          disabled={isLaunching}
          className="min-w-[240px] py-3 text-base"
        >
          <Rocket className="w-5 h-5" />
          {isSkip ? 'Save & Skip' : 'Start Dippy'}
        </Button>
      </div>
    </div>
  );
}
